import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import {
  getRolesAPI,
  getUserDetailAPI,
  getUserListAPI,
  patchActivateAccountAPI,
  patchDeactivateAccountAPI,
  postUserAPI,
  putResetAccountAPI,
  putUserAPI,
} from 'features/Account/services/user'
import { serializeQueryToURL } from 'utils/queryParamsURL'
import { toastSuccess } from 'utils/toast'
import { getLocationDetail } from 'features/Location/services/location'
import type { AppDispatchType } from 'store'

export const SLICE_NAME = 'ADMIN_ACCESS/user'

export const fetchGetLocationSelected = createAsyncThunk(
  `${SLICE_NAME}/fetchGetLocationSelected`,
  async (locationIds: number[], { rejectWithValue }) => {
    try {
      const locationPromises = locationIds.map((locationId) => getLocationDetail(locationId))
      const res = await Promise.all(locationPromises)

      return res.map(({ data }) => ({
        location_id: data.location_id,
        location_name: data.location_name,
        location_type: data.location_type,
      }))
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchGetUserList = createAsyncThunk(
  `${SLICE_NAME}/fetchGetUserList`,
  async (
    { params, isVendor }: { params: Parameters<typeof getUserListAPI>[0]; isVendor: boolean },
    { rejectWithValue },
  ) => {
    try {
      const res = await getUserListAPI(params, isVendor)

      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchGetUserDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchGetUserDetail`,
  async (
    { userId, isVendor }: { userId: Parameters<typeof getUserDetailAPI>[0]; isVendor: boolean },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await getUserDetailAPI(userId, isVendor)

      if (res.data.data.roles.length) {
        const locationIds = res.data.data.roles[0].location_ids
        dispatch(fetchGetLocationSelected(locationIds))
      }

      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const actGetUserList =
  ({ params, isVendor }: { params?: Parameters<typeof getUserListAPI>[0]; isVendor: boolean }) =>
  (dispatch: StoreDispatchType) => {
    const qs = serializeQueryToURL({ page_index: 0, ...params })
    dispatch(fetchGetUserList({ params: qs, isVendor }))
  }

export const fetchPostUser = createAsyncThunk(
  `${SLICE_NAME}/fetchPostUser`,
  async (
    { callback, isVendor }: { callback: (() => void) | undefined; isVendor: boolean },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const { userAdminAccess } = getState() as StoreStateType
      const { form } = userAdminAccess

      const payload: Parameters<typeof postUserAPI>[0] = {
        name: form.name,
        email: form.email,
        phone_number: form.phoneNumber,
        position: form.position,
        locations: form.locations.map((item) => ({
          id: item.location_id as number,
        })),
        roles: [
          {
            id: form.roles?.id as number,
          },
        ],
        is_super_admin: false,
      }

      const res = await postUserAPI(payload, isVendor)

      if (callback) {
        callback()
      }

      ;(dispatch as AppDispatchType)(actGetUserList({ isVendor }))

      toastSuccess(`User ${form.name} berhasil ditambahkan`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPutUser = createAsyncThunk(
  `${SLICE_NAME}/fetchPutUser`,
  async (
    { callback, isVendor }: { callback: (() => void) | undefined; isVendor: boolean },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const { userAdminAccess } = getState() as StoreStateType
      const {
        form,
        feature: {
          drawerAddAccount: { data },
        },
      } = userAdminAccess

      const mapIdsToStatus = (
        currentIds: number[],
        selectedItems: number[],
      ): {
        id: number
        is_deleted: boolean
      }[] => {
        const selectedIds = new Set(selectedItems)

        return Array.from(new Set([...currentIds, ...selectedItems])).map((id) => ({
          id,
          is_deleted: !selectedIds.has(id),
        }))
      }

      const payload: Parameters<typeof putUserAPI>[0]['payload'] = {
        name: form.name,
        email: form.email,
        phone_number: form.phoneNumber,
        position: form.position,
        locations: mapIdsToStatus(
          data?.roles.length ? data.roles[0].location_ids : [],
          form.locations.map((item) => item.location_id),
        ),
        roles: mapIdsToStatus(
          data?.roles.length ? [data.roles[0].id] : [],
          form.roles ? [form.roles.id] : [],
        ),
        is_super_admin: false,
      }

      const res = await putUserAPI({
        id: form.id as number,
        payload,
        isVendor,
      })

      if (callback) {
        callback()
      }

      ;(dispatch as AppDispatchType)(actGetUserList({ isVendor }))

      toastSuccess(`User ${form.name} berhasil diubah`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPutResetAccount = createAsyncThunk(
  `${SLICE_NAME}/fetchPutResetAccount`,
  async (
    data: {
      payload: { phoneNumber: string }
      callback?: () => void
      isVendor: boolean
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { payload, callback, isVendor } = data
      const res = await putResetAccountAPI(
        {
          phone_number: payload.phoneNumber,
        },
        isVendor,
      )

      if (callback) {
        callback()
      }

      ;(dispatch as AppDispatchType)(actGetUserList({ isVendor }))

      toastSuccess(`Akun Berhasil di Reset`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPatchActivateAccount = createAsyncThunk(
  `${SLICE_NAME}/fetchPatchActivateAccount`,
  async (
    {
      params,
      isVendor,
    }: {
      params: {
        userId: number
        name: string
      }
      isVendor: boolean
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { userId, name } = params
      const res = await patchActivateAccountAPI(userId, isVendor)

      ;(dispatch as AppDispatchType)(actGetUserList({ isVendor }))
      toastSuccess(`User ${name} berhasil diaktifkan`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPatchDeactivateAccount = createAsyncThunk(
  `${SLICE_NAME}/fetchPatchDeactivateAccount`,
  async (
    payload: {
      params: {
        userId: number
        name: string
      }
      callback?: () => void
      isVendor: boolean
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { params, callback, isVendor } = payload
      const { userId, name } = params
      const res = await patchDeactivateAccountAPI(userId, isVendor)

      ;(dispatch as AppDispatchType)(actGetUserList({ isVendor }))
      if (callback) {
        callback()
      }
      toastSuccess(`User ${name} berhasil dinon-aktifkan`)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchGetRoles = createAsyncThunk(
  `${SLICE_NAME}/fetchGetRoles`,
  async (
    { isVendor, params }: { params?: Parameters<typeof getRolesAPI>[0]; isVendor: boolean },
    { rejectWithValue },
  ) => {
    try {
      const res = await getRolesAPI(params, isVendor)
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
